/**
 * Background Job — create / edit, as its own PAGE.
 *
 * This was a modal inside the Jobs tab. It follows the shape every other master
 * form in the app uses (error-master, log-master, notification-master …): a route
 * of its own, a back arrow, and `useNavigationBlocker` + `DiscardChangesDialog` so
 * leaving with unsaved edits asks first. A modal could be dismissed by a stray
 * click on the backdrop and the work was gone without a word.
 *
 * Two rules from the integration guide still drive the form:
 *  · `executionConfig` is polymorphic AND strict (`extra="forbid"`), so the config
 *    is rebuilt from scratch whenever executionType changes. Merging guarantees 422.
 *  · `jobKey` is immutable after creation — it is the handle schedules and run
 *    history refer to — so the edit form renders it disabled.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { InputSwitch } from 'sharedUi/InputSwitch';
import { Input } from 'sharedUi/Input';
import { TextArea } from 'sharedUi/TextArea';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Message } from 'sharedUi/Message';
import { backgroundJobService } from '../../services/background-job.service';
import { projectsService, type AccessibleProject } from '../../services/projects.service';
import type {
  BackgroundJobCreate, ExecutionConfig, ExecutionType,
} from '../../types/background-job.types';
import {
  FUNCTION_KEY_FALLBACK, HTTP_METHODS, blankConfig, jobKeyError,
} from './tabs/shared';
import { JsonField } from '../../components/common/json-field';
import FormField from '../../components/common/form-field';
import NumberStepper from '../../components/common/number-stepper';
import { useToastApi } from '../../hooks/use-toast-api';
import { useExecutionTypes } from './tabs/use-job-value-sets';
import { useJobPermissions } from './tabs/use-job-permissions';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import ScreenHeader from '../../components/common/screen-header';

const LIST_ROUTE = '/background-jobs?tab=jobs';

const EMPTY_JOB: BackgroundJobCreate = {
  jobKey: '',
  jobName: '',
  description: '',
  executionType: 'FUNCTION',
  isActive: true,
  maxRetries: 0,
  retryIntervalSeconds: 0,
  // null, not 0: absent means "inherit the project's window", while 0 is the
  // explicit instruction to keep this job's history for ever.
  runRetentionDays: null as number | null,
  defaultParameters: {},
  executionConfig: blankConfig('FUNCTION'),
};

const JobFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const { canCreate, canUpdate } = useJobPermissions();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();
  const { options: executionTypeOptions } = useExecutionTypes();

  const [form, setForm] = useState<BackgroundJobCreate>(EMPTY_JOB);
  const [submitted, setSubmitted] = useState(false);

  // The projects this caller may file a job under. Failure is silent and leaves
  // the list empty, which hides the field — a job saves fine without an appId,
  // so a lookup that did not load must not block the form.
  const [projects, setProjects] = useState<AccessibleProject[]>([]);
  useEffect(() => {
    projectsService.getAccessible().then(setProjects).catch(() => setProjects([]));
  }, []);
  const canChooseProject = projects.length > 1;
  /* Shared is not offered. A job with no project runs for every project, and the
     ones that legitimately do — the platform's own heartbeat and sweeps — are
     seeded, not authored here. An existing shared job still shows as such. */
  const projectOptions = useMemo(() => ([
    ...(form.appId ? [] : [{ label: 'Shared (all projects)', value: '' }]),
    ...projects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id })),
  ]), [projects, form.appId]);
  // The snapshot the dirty check compares against: EMPTY_JOB on create, the loaded
  // job on edit. Without it every edit page would open already "dirty" and ask to
  // discard changes the user never made.
  const initialRef = useRef<string>(JSON.stringify(EMPTY_JOB));
  // Set the moment a save succeeds, so the redirect that follows is not itself
  // treated as an unsaved-changes exit.
  const savedRef = useRef(false);

  const jobQ = useQuery({
    queryKey: ['bg-job', id],
    queryFn: () => backgroundJobService.getJob(id!),
    enabled: isEditMode,
  });

  /* The registry the executor actually validates against, rather than a copy of
     it in this file. FUNCTION_KEY_FALLBACK is only the last resort, so the form
     still renders something if this call fails. */
  const functionKeysQ = useQuery({
    // Keyed by the job's project: changing it changes which functions exist.
    queryKey: ['background-jobs', 'function-keys', form.appId || ''],
    queryFn: () => backgroundJobService.listFunctionKeys(form.appId),
    staleTime: 3e5,
  });
  const functionKeyOptions = (functionKeysQ.data?.functionKeys || []).length
    ? functionKeysQ.data!.functionKeys.map((k) => ({ label: k, value: k }))
    : FUNCTION_KEY_FALLBACK;

  useEffect(() => {
    const j = jobQ.data;
    if (!j) return;
    const loaded: BackgroundJobCreate = {
      jobKey: j.jobKey,
      jobName: j.jobName,
      description: j.description || '',
      // Carried through the round trip: without it an edit reopens showing
      // "Shared" for a job that belongs to a project, and saving would move it.
      appId: j.appId || undefined,
      executionType: j.executionType,
      isActive: j.isActive,
      maxRetries: j.maxRetries,
      retryIntervalSeconds: j.retryIntervalSeconds,
      runRetentionDays: j.runRetentionDays ?? null,
      defaultParameters: j.defaultParameters || {},
      executionConfig: j.executionConfig || blankConfig(j.executionType),
    };
    setForm(loaded);
    initialRef.current = JSON.stringify(loaded);
  }, [jobQ.data]);

  const isDirty = !savedRef.current && JSON.stringify(form) !== initialRef.current;
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } =
    useNavigationBlocker({ when: isDirty });

  const saveM = useMutation({
    mutationFn: (body: BackgroundJobCreate) => (isEditMode
      // jobKey is never sent on update — it cannot change
      ? backgroundJobService.updateJob(id!, { ...body, jobKey: undefined } as never)
      : backgroundJobService.createJob(body)),
    onSuccess: () => {
      ok(isEditMode ? 'Job updated' : 'Job created');
      savedRef.current = true;
      qc.invalidateQueries({ queryKey: ['bg-jobs'] });
      qc.invalidateQueries({ queryKey: ['bg-schedules'] });
      qc.invalidateQueries({ queryKey: ['bg-dashboard'] });
      // savedRef silences the blocker, so this leaves without a prompt.
      handleNavigate(LIST_ROUTE);
    },
    onError: (e) => fail(e),
  });

  /** Switching type must DISCARD the old config — a stale key is a 422. */
  const changeType = (executionType: ExecutionType) =>
    setForm((f) => ({ ...f, executionType, executionConfig: blankConfig(executionType) }));

  const setCfg = (patch: ExecutionConfig) =>
    setForm((f) => ({ ...f, executionConfig: { ...f.executionConfig, ...patch } }));

  const cfg = form.executionConfig as Record<string, unknown>;

  const configError = useMemo(() => {
    if (form.executionType === 'FUNCTION' && !cfg.functionKey) return 'Pick a function.';
    if (form.executionType === 'LLM_PROMPT' && !String(cfg.promptTemplate || '').trim()) return 'A prompt template is required.';
    if (form.executionType === 'API_CALL') {
      const hasUrl = !!String(cfg.url || '').trim();
      const hasRef = !!String(cfg.connectionRef || '').trim();
      // cross-field rule: exactly one of url / connectionRef+path
      if (hasUrl && hasRef) return 'Give either a URL or a connection reference, not both.';
      if (!hasUrl && !hasRef) return 'A URL is required.';
    }
    return '';
  }, [form.executionType, cfg]);

  const keyError = isEditMode ? '' : jobKeyError(form.jobKey);
  const invalid = !form.jobName.trim() || !!keyError || !!configError;

  const submit = () => {
    setSubmitted(true);
    if (invalid) return;
    if (!isEditMode) {
      // the key is the handle schedules and run history refer to — it cannot be changed later
      confirmDialog({
        message: `Create job with key "${form.jobKey}"? The key cannot be changed afterwards.`,
        header: 'Confirm job key', icon: 'pi pi-info-circle',
        acceptLabel: 'Create', rejectLabel: 'Go back',
        accept: () => saveM.mutate(form),
      });
      return;
    }
    saveM.mutate(form);
  };

  const allowed = isEditMode ? canUpdate : canCreate;
  if (!allowed) {
    return (
      <div className="py-4 px-4 lg:px-6">
        <Message severity="warn" className="w-full justify-start"
          text="You do not have permission to open this form." />
      </div>
    );
  }

  return (
    <>
      {toast}
      <ConfirmDialog />
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-BackgroundJob-Form">
        {/* Header — the back arrow goes through handleNavigate so it is guarded too */}
        <ScreenHeader
          icon="pi pi-briefcase"
          title={isEditMode ? `Edit ${form.jobKey || 'background job'}` : 'New background job'}
          subtitle={isEditMode ? 'Update what this job runs and how it retries' : 'Define what should run'}
          onBack={() => handleNavigate(LIST_ROUTE)}
        />

        <div className="rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          {/* A 12-column grid, so the identity fields can be sized to their content
              rather than each taking half the form. A job key is ~20 characters and
              a name a short phrase; at half-width each they were mostly empty space,
              which read as "type a lot here". */}
          <div className="grid grid-cols-12 items-start gap-x-4 gap-y-4">
            <FormField className="col-span-12 sm:col-span-4" label="Job Key" required
              error={submitted && !isEditMode ? keyError : undefined}
              hint={isEditMode
                ? 'The key cannot change — schedules and run history refer to it.'
                : 'Upper case, digits and underscores. Cannot be changed later.'}>
              <Input name="jobKey" value={form.jobKey} disabled={isEditMode}
                onChange={(v: string) => setForm((f) => ({ ...f, jobKey: v.toUpperCase() }))}
                placeholder="PURGE_TOKENS" className="w-full" />
            </FormField>

            <FormField className="col-span-9 sm:col-span-5" label="Name" required
              error={submitted && !form.jobName.trim() ? 'A name is required.' : undefined}>
              <Input name="jobName" value={form.jobName}
                onChange={(v: string) => setForm((f) => ({ ...f, jobName: v }))}
                placeholder="Purge expired refresh tokens" className="w-full" />
            </FormField>

            {/* Active sits beside the name: it belongs to the job's identity, not to
                its retry mechanics, and it is the field most often flipped on its own. */}
            <FormField className="col-span-3 sm:col-span-3" label="Active">
              <div className="flex h-[38px] items-center">
                <InputSwitch checked={form.isActive ?? true}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: !!e.value }))} />
              </div>
            </FormField>

            <FormField className="col-span-12" label="Description">
              <Input name="description" value={form.description || ''}
                onChange={(v: string) => setForm((f) => ({ ...f, description: v }))} className="w-full" />
            </FormField>

            {/* Owning project. The API takes this from the body and validates it
                against project_master (404 on an unknown id), so unlike the
                schedule screens this is a real choice rather than a display of
                one. Blank is a legitimate value — a job with no appId is shared
                across every project — which is why there is no "required" here
                and why the option is spelled out rather than left as a
                placeholder. Hidden when the caller reaches only one project:
                the answer is then forced, and a control with one option is a
                question that should not have been asked. */}
            {canChooseProject && (
              <FormField className="col-span-12 sm:col-span-4" label="Project"
                hint="Which project owns this job.">
                <Dropdown value={form.appId || ''} options={projectOptions}
                  optionLabel="label" optionValue="value" className="w-full"
                  onChange={(e: { value: string }) => setForm((f) => ({ ...f, appId: e.value || undefined }))}
                  data-testid="JobForm.Project" />
              </FormField>
            )}

            <FormField className="col-span-12 sm:col-span-4" label="Execution Type" required
              hint="Changing this clears the configuration below.">
              <Dropdown value={form.executionType} options={executionTypeOptions}
                optionLabel="label" optionValue="value" className="w-full"
                onChange={(e: { value: ExecutionType }) => changeType(e.value)} />
            </FormField>

            <FormField className="col-span-4 sm:col-span-2" label="Max retries">
              <NumberStepper value={form.maxRetries ?? 0} min={0} max={100}
                ariaLabel="Max retries"
                onChange={(v) => setForm((f) => ({ ...f, maxRetries: v ?? 0 }))} />
            </FormField>

            <FormField className="col-span-4 sm:col-span-2" label="Retry gap (s)">
              <NumberStepper value={form.retryIntervalSeconds ?? 0} min={0} max={86400}
                ariaLabel="Retry gap in seconds"
                onChange={(v) => setForm((f) => ({ ...f, retryIntervalSeconds: v ?? 0 }))} />
            </FormField>

            {/* Per-JOB retention. Retention is a property of what a job DOES, not
                of who owns it: a reconciliation may need a year of history while a
                health ping needs a day, and both live in the same project. Blank
                inherits the project's window; 0 keeps forever. */}
            <FormField className="col-span-4 sm:col-span-4" label="Keep run history (days)">
              <NumberStepper
                value={form.runRetentionDays ?? null}
                min={0}
                max={3650}
                placeholder="inherit"
                ariaLabel="Keep run history in days"
                onChange={(v) => setForm((f) => ({ ...f, runRetentionDays: v }))} />
            </FormField>

            {/* ── type-specific config ── */}
            <div className="col-span-12 rounded-md border border-gray-200 p-3 dark:border-gray-700">
              <div className="mb-2 text-xs font-semibold uppercase text-gray-500">
                {form.executionType} configuration
              </div>

              {form.executionType === 'FUNCTION' && (
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Function" required
                    hint="A server-side allowlist — adding one is a code change.">
                    <Dropdown value={(cfg.functionKey as string) || ''} options={functionKeyOptions}
                      optionLabel="label" optionValue="value" placeholder="Select a function" className="w-full"
                      onChange={(e: { value: string }) => setCfg({ functionKey: e.value })} />
                  </FormField>
                  <FormField label="Timeout (s)">
                    <NumberStepper value={(cfg.timeoutSeconds as number) ?? 60} min={1} max={3600}
                      ariaLabel="Timeout in seconds"
                      onChange={(v) => setCfg({ timeoutSeconds: v ?? 60 })} />
                  </FormField>
                </div>
              )}

              {form.executionType === 'API_CALL' && (
                <div className="grid grid-cols-4 gap-3">
                  <FormField label="Method">
                    <Dropdown value={(cfg.method as string) || 'GET'} options={HTTP_METHODS}
                      optionLabel="label" optionValue="value" className="w-full"
                      onChange={(e: { value: string }) => setCfg({ method: e.value })} />
                  </FormField>
                  <FormField className="col-span-3" label="URL" required
                    hint="Paste the address only — surrounding quotes become part of the value and the request fails.">
                    <Input name="url" value={(cfg.url as string) || ''} className="w-full"
                      placeholder="https://partner.example.com/sync"
                      onChange={(v: string) => setCfg({ url: v })} />
                  </FormField>
                  <FormField label="Timeout (s)">
                    <NumberStepper value={(cfg.timeoutSeconds as number) ?? 30} min={1} max={3600}
                      ariaLabel="Timeout in seconds"
                      onChange={(v) => setCfg({ timeoutSeconds: v ?? 30 })} />
                  </FormField>
                  <FormField className="col-span-3" label="Expected status"
                    hint="Any other status fails the run.">
                    <Input name="expectedStatus" value={((cfg.expectedStatus as number[]) || [200]).join(', ')}
                      className="w-full" placeholder="200, 202"
                      onChange={(v: string) => setCfg({
                        expectedStatus: v.split(',').map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n)),
                      })} />
                  </FormField>
                  <JsonField className="col-span-2" label="Headers" value={cfg.headers}
                    onChange={(o) => setCfg({ headers: o })} />
                  <JsonField className="col-span-2" label="Body template" value={cfg.bodyTemplate}
                    onChange={(o) => setCfg({ bodyTemplate: o })} />
                </div>
              )}

              {form.executionType === 'LLM_PROMPT' && (
                <div className="grid grid-cols-4 gap-3">
                  <FormField className="col-span-4" label="Prompt template" required
                    hint="{{today}}, {{now}}, {{timestamp}} and any default parameter can be used as placeholders.">
                    <TextArea rows={3} value={(cfg.promptTemplate as string) || ''}
                      onChange={(v: string) => setCfg({ promptTemplate: v })}
                      placeholder="Summarise incidents since {{lastRunAt}}"
                      className="w-full text-sm" />
                  </FormField>
                  <FormField className="col-span-2" label="Model">
                    <Input name="model" value={(cfg.model as string) || ''} className="w-full"
                      placeholder="executor default" onChange={(v: string) => setCfg({ model: v || undefined })} />
                  </FormField>
                  <FormField label="Max tokens">
                    {/* step 64: nudging a token budget one at a time is useless */}
                    <NumberStepper value={(cfg.maxTokens as number) ?? 1024} min={1} max={200000} step={64}
                      ariaLabel="Max tokens"
                      onChange={(v) => setCfg({ maxTokens: v ?? 1024 })} />
                  </FormField>
                  <FormField label="Output key">
                    <Input name="outputKey" value={(cfg.outputKey as string) || 'output'} className="w-full"
                      onChange={(v: string) => setCfg({ outputKey: v })} />
                  </FormField>
                  <Message className="col-span-4 w-full justify-start" severity="warn"
                    text="No LLM client is registered in this deployment yet — these jobs will fail with a message naming the missing registration." />
                </div>
              )}

              {submitted && configError && <div className="mt-2 text-xs text-red-500">{configError}</div>}
            </div>

            <JsonField className="col-span-12" label="Default parameters"
              hint="Overridden by a schedule's parameters, then by a manual run's."
              value={form.defaultParameters}
              onChange={(o) => setForm((f) => ({ ...f, defaultParameters: o || {} }))} />
          </div>

          <div className="mt-4 flex justify-end gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
            <Button label="Cancel" severity="secondary" outlined
              onClick={() => handleNavigate(LIST_ROUTE)} />
            <Button label={isEditMode ? 'Save' : 'Create'} icon="pi pi-check"
              onClick={submit} disabled={saveM.isPending || (isEditMode && jobQ.isLoading)} />
          </div>
        </div>
      </div>

      <DiscardChangesDialog visible={showLeaveDialog} onStay={cancelLeave} onLeave={confirmLeave}
        testId="BackgroundJobForm.Dialog.Leave" />
    </>
  );
};

export default JobFormPage;
